import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FlowguardLogo } from "@/components/FlowguardLogo";
import { 
  Check, Crown, Zap, ArrowLeft, Loader2,
  CreditCard, Shield
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import { toast } from "sonner";
import axios from "axios";

const createUpgradeRequestId = () =>
  globalThis.crypto?.randomUUID?.()
  || "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });

const UpgradePage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [upgradeQuote, setUpgradeQuote] = useState(null);
  useEffect(() => {
    const paymentResult = searchParams.get('payment');
    const provider = searchParams.get('provider');
    if (paymentResult === 'cancelled' && provider === 'lemonsqueezy') {
      toast.error("Checkout cancelled. No charges were made.");
      navigate("/upgrade", { replace: true });
    }
  }, [navigate, searchParams]);

  const handleUpgrade = async (plan) => {
    if (!token) {
      navigate('/login');
      return;
    }

    const currentTier = user?.tier === "light" ? "free" : (user?.tier || "free");
    if (currentTier === "pro" && plan === "starter") {
      navigate('/settings');
      return;
    }

    setLoading(true);
    setSelectedPlan(plan);
    if (currentTier === "starter" && plan === "pro") {
      try {
        const response = await axios.post(
          `${API}/billing/stripe/upgrade-to-pro/preview`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setUpgradeQuote(response.data);
        setLoading(false);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Your Pro upgrade price could not be prepared.");
        setLoading(false);
        setSelectedPlan(null);
      }
      return;
    }

    navigate(`/billing/checkout?plan=${plan}`);
  };

  const formatQuoteAmount = (quote) => {
    if (!quote) return "";
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: String(quote.currency || "USD").toUpperCase(),
    }).format(Number(quote.amount_due || 0) / 100);
  };

  const confirmProUpgrade = async () => {
    if (!upgradeQuote || loading) return;
    setLoading(true);
    setSelectedPlan("pro");
    try {
      const response = await axios.post(
        `${API}/billing/stripe/upgrade-to-pro`,
        {
          upgrade_request_id: createUpgradeRequestId(),
          quote_id: upgradeQuote.quote_id,
          confirmed_amount_due: upgradeQuote.amount_due,
          confirmed_currency: upgradeQuote.currency,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUpgradeQuote(null);
      if (response.data.status === "payment_required") {
        if (response.data.invoice_url) {
          window.location.assign(response.data.invoice_url);
          return;
        }
        toast.error("Complete the pending upgrade payment from Settings before Pro can be activated.");
        navigate("/settings");
        return;
      }
      sessionStorage.setItem(
        "flowguard_pending_upgrade",
        JSON.stringify({ plan: "pro", provider: "stripe", createdAt: Date.now() })
      );
      navigate("/dashboard?payment=processing&provider=stripe");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Your Pro upgrade could not be completed.");
      setLoading(false);
      setSelectedPlan(null);
      setUpgradeQuote(null);
    }
  };

  const plans = [
    {
      id: "starter_monthly",
      plan: "starter",
      name: "Starter Investor",
      price: "$9.99",
      period: "/month",
      description: "Save up to 50 deals and generate Flowguard-branded reports",
      icon: Zap,
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
      borderColor: "border-emerald-500/30",
      features: [
        "Everything in Free",
        "50 saved deals",
        "5 PDF reports per month",
        "Rental and BRRRR workflows",
        "Deal notes and saved assumptions",
        "Email support"
      ],
      cta: "Upgrade to Starter"
    },
    {
      id: "pro_monthly",
      plan: "pro",
      name: "Pro Investor",
      price: "$29.99",
      period: "/month",
      description: "Unlimited workflows for active investors and small teams",
      icon: Crown,
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
      borderColor: "border-amber-500",
      popular: true,
      features: [
        "Everything in Starter",
        "Unlimited saved deals",
        "Advanced analysis tools",
        "Deal comparison dashboard",
        "Portfolio and report workflows",
        "Unlimited PDF reports",
        "White-label investor-ready reports",
        "Priority support (24hr)"
      ],
      cta: "Upgrade to Pro"
    }
  ];

  const getDisplayPlan = (tier) => {
    const normalizedTier = tier === "light" ? "free" : (tier || "free");
    if (normalizedTier === "starter") return "Starter Investor";
    if (normalizedTier === "pro") return "Pro Investor";
    if (normalizedTier === "developer") return "Developer (legacy)";
    return "Free Investor";
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800 sticky top-0 z-50 bg-slate-900/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard">
              <FlowguardLogo size="md" />
            </Link>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Free handles first-pass screening. Starter adds reports and higher limits.
            Pro unlocks unlimited analysis, comparison, and portfolio workflows.
          </p>
          
          {/* Current Plan Badge */}
          {user?.tier && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 border border-slate-700">
              <Crown className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-slate-300">
                Current plan: <span className="text-white font-medium">{getDisplayPlan(user?.tier)}</span>
              </span>
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const normalizedTier = user?.tier === "light" ? "free" : (user?.tier || "free");
            const isCurrentPlan = normalizedTier === plan.plan;
            const requiresCurrentPlanCancellation = normalizedTier === "pro" && plan.plan === "starter";
            const isLoading = loading && selectedPlan === plan.plan;
            const ctaLabel = requiresCurrentPlanCancellation
              ? "Manage current subscription"
              : plan.cta;
            
            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-6 lg:p-8 transition-all ${
                  plan.popular
                    ? `bg-gradient-to-b from-amber-500/10 to-slate-800 border-2 ${plan.borderColor} scale-105 shadow-xl`
                    : `bg-slate-800 border ${plan.borderColor}`
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-amber-500 text-black text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap">
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <div className={`w-12 h-12 rounded-xl ${plan.bgColor} flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6 ${plan.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-3xl font-bold text-white">{plan.price}</span>
                    <span className="text-slate-400">{plan.period}</span>
                  </div>
                  <p className={`text-sm ${plan.popular ? 'text-amber-400' : 'text-slate-400'}`}>
                    {plan.description}
                  </p>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className={`w-5 h-5 ${plan.color} flex-shrink-0 mt-0.5`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleUpgrade(plan.plan)}
                  disabled={isLoading || isCurrentPlan}
                  className={`w-full ${
                    plan.popular
                      ? "bg-amber-500 hover:bg-amber-600 text-black font-bold"
                      : "bg-slate-700 hover:bg-slate-600 text-white"
                  } ${isCurrentPlan ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : isCurrentPlan ? (
                    "Current Plan"
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      {ctaLabel}
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Trust Badges */}
        <div className="mt-12 flex flex-wrap justify-center gap-8 text-slate-400 text-sm">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-500" />
            <span>Secure payment via Stripe</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-emerald-500" />
            <span>Cancel anytime; access continues through your paid period</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-emerald-500" />
            <span>Subscription charges are non-refundable</span>
          </div>
        </div>
      </main>

      <Dialog
        open={Boolean(upgradeQuote)}
        onOpenChange={(open) => {
          if (!open && !loading) {
            setUpgradeQuote(null);
            setSelectedPlan(null);
          }
        }}
      >
        <DialogContent className="border-slate-700 bg-slate-900 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm your Pro upgrade</DialogTitle>
            <DialogDescription className="leading-6 text-slate-400">
              Stripe will charge the prorated upgrade amount immediately. Your subscription will renew on Pro at the regular Pro monthly price.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-300">Charged now</p>
            <p className="mt-2 text-3xl font-bold text-white">{formatQuoteAmount(upgradeQuote)}</p>
            <p className="mt-2 text-sm leading-6 text-slate-300">
              By confirming, you authorize this immediate prorated charge and reactivate your subscription if cancellation was scheduled.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              disabled={loading}
              onClick={() => {
                setUpgradeQuote(null);
                setSelectedPlan(null);
              }}
              className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white"
            >
              Keep Starter
            </Button>
            <Button
              disabled={loading}
              onClick={confirmProUpgrade}
              className="bg-amber-500 font-bold text-black hover:bg-amber-400"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
              Confirm and pay {formatQuoteAmount(upgradeQuote)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UpgradePage;
